import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Globe2, Check, X } from "lucide-react";
import { LANGUAGES, flagUrl, flagUrl2x } from "@/i18n";
import { pushOverlay, popOverlay } from "@/lib/overlayState";

function FlagImg({ code, size = 18, className = "" }) {
  return (
    <img
      src={flagUrl(code)}
      srcSet={`${flagUrl(code)} 1x, ${flagUrl2x(code)} 2x`}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`inline-block rounded-full object-cover shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        boxShadow: "0 0 0 1px color-mix(in srgb, var(--text-1) 12%, transparent)",
      }}
    />
  );
}

/**
 * Friendly LanguageSwitcher
 *  - Desktop: subtle button → opens a small popover with flag + native name + region hint
 *  - Mobile: button → opens a centered bottom sheet (modal) covering full width — large touch targets
 */
export default function LanguageSwitcher({ className = "", variant = "desktop" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  useEffect(() => {
    if (variant !== "desktop") return;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [variant]);

  // Lock scroll + hide Tawk chat bubble while the mobile sheet is
  // open. Tawk's launcher iframe has an internal z-index of ~1e6 so
  // it would otherwise sit on top of our backdrop.
  useEffect(() => {
    if (variant === "mobile" && open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      pushOverlay();
      return () => {
        document.body.style.overflow = prev;
        popOverlay();
      };
    }
  }, [open, variant]);

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  // Desktop chip + popover
  if (variant === "desktop") {
    return (
      <div ref={popRef} className={`relative ${className}`}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="language-switcher-desktop"
          aria-label={t("lang.change", "Change language")}
          aria-expanded={open}
          className="h-9 inline-flex items-center gap-2 px-2.5 rounded-full border border-[color:var(--line)] hover:border-brand/50 hover:bg-[color:var(--surface-2)] transition-colors text-xs"
        >
          <FlagImg code={current.flag} />
          <span className="hidden xl:inline text-[color:var(--text-1)] font-medium">{current.label}</span>
          <span className="xl:hidden text-[color:var(--text-2)] uppercase font-mono text-[12px]">{current.code}</span>
        </button>

        {open && (
          <div
            data-testid="language-menu-desktop"
            className="absolute right-0 mt-2 w-[280px] rounded-2xl border border-[color:var(--line)] p-2 z-50 shadow-2xl"
            style={{ background: "var(--surface)", backdropFilter: "blur(20px)" }}
          >
            <div className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-3)] flex items-center gap-1.5">
              <Globe2 size={11} className="text-brand" />
              {t("nav.language", "Language")}
            </div>
            {/* Two-column compact grid — flag + native name, no extras */}
            <div className="grid grid-cols-2 gap-1">
              {LANGUAGES.map((lang) => {
                const active = lang.code === i18n.language;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => change(lang.code)}
                    data-testid={`lang-${lang.code}-desktop`}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12px] transition-colors
                      ${active
                        ? "bg-brand/10 text-[color:var(--text-1)] ring-1 ring-brand/40"
                        : "text-[color:var(--text-2)] hover:bg-[color:var(--line)] hover:text-[color:var(--text-1)]"}`}
                  >
                    <FlagImg code={lang.flag} size={16} />
                    <span className="font-medium truncate flex-1 text-left">{lang.label}</span>
                    {active && <Check size={11} className="text-brand shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Mobile — compact chip → full-screen bottom sheet
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="language-switcher-mobile"
        aria-label={t("lang.change", "Change language")}
        className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-[color:var(--line)] hover:border-brand/50 transition-colors"
      >
        <FlagImg code={current.flag} size={18} />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
          data-testid="language-sheet-overlay"
          style={{ width: "100vw", height: "100dvh" }}
        >
          <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" />
          <div
            data-testid="language-menu-mobile"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl border border-[color:var(--line)] p-5 max-h-[80vh] overflow-y-auto"
            style={{ background: "var(--surface)" }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe2 size={16} className="text-brand" />
                <span className="font-display text-base font-medium text-[color:var(--text-1)]">
                  {t("nav.language", "Language")}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("common.close", "Close")}
                data-testid="language-menu-mobile-close"
                className="w-8 h-8 rounded-full border border-[color:var(--line)] grid place-items-center text-[color:var(--text-2)]"
              >
                <X size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {LANGUAGES.map((lang) => {
                const active = lang.code === i18n.language;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => change(lang.code)}
                    data-testid={`lang-${lang.code}-mobile`}
                    className={`flex items-center gap-2.5 px-3 py-3 rounded-xl transition-colors text-left
                      ${active ? "bg-brand/10 ring-1 ring-brand/40 text-[color:var(--text-1)]" : "hover:bg-[color:var(--line)] text-[color:var(--text-2)]"}`}
                  >
                    <FlagImg code={lang.flag} size={20} />
                    <span className="text-[color:var(--text-1)] font-medium text-sm flex-1 truncate">{lang.label}</span>
                    {active && <Check size={14} className="text-brand shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
